"use client";

import React from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  ChevronRight,
  RefreshCw,
  Clock,
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  ArrowRight,
  Sparkles,
  PhoneCall,
  Mail,
  FileText,
  Calendar,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";

const SimplePageBackground = dynamic(
  () => import("@/components/SimplePageBackground").then((m) => m.SimplePageBackground),
  { ssr: true }
);

const IKFooter = dynamic(
  () => import("@/components/IKFooter").then((m) => m.IKFooter),
  { ssr: true }
);

const HIGHLIGHT_CARDS = [
  {
    icon: Clock,
    title: "Free Cancellation",
    desc: "100% full refund when you cancel at least 24 hours prior to your scheduled pickup time.",
    badge: "Most Flexible",
  },
  {
    icon: RefreshCw,
    title: "Fast Refund SLA",
    desc: "Approved refunds are credited directly back to your original payment method within 3 to 7 business days.",
    badge: "3-7 Days",
  },
  {
    icon: ShieldCheck,
    title: "100% Deposit Refund",
    desc: "Your security deposit is fully refunded post-trip upon vehicle inspection and toll clearance.",
    badge: "Transparent",
  },
  {
    icon: Calendar,
    title: "Free Modifications",
    desc: "Reschedule or modify your trip dates and times free of charge up to 12 hours before pickup.",
    badge: "Easy Process",
  },
];

const CANCELLATION_TIERS = [
  {
    timeframe: "More than 24 hours before pickup",
    refundPercent: "100% Refund",
    fee: "No Fee (0%)",
    status: "success",
    note: "Full refund of rental charges to your original payment source.",
  },
  {
    timeframe: "12 to 24 hours before pickup",
    refundPercent: "90% Refund",
    fee: "10% Fee",
    status: "warning",
    note: "Small processing fee deducted to cover fleet reservation hold.",
  },
  {
    timeframe: "6 to 12 hours before pickup",
    refundPercent: "50% Refund",
    fee: "50% Fee",
    status: "warning",
    note: "50% deduction applied as vehicle preparation has commenced.",
  },
  {
    timeframe: "Less than 6 hours / No-Show",
    refundPercent: "0% Refund",
    fee: "100% Fee",
    status: "error",
    note: "Non-refundable as the vehicle is dispatched and reserved solely for you.",
  },
];

const POLICY_SECTIONS = [
  {
    id: "deposit-policy",
    icon: CreditCard,
    title: "1. Security Deposit Refund Policy",
    content: [
      "A refundable security deposit is held at the commencement of your trip to cover potential highway tolls (FASTag), traffic violation challans, or minor damages.",
      "Upon returning the vehicle, our team conducts a swift physical inspection and checks highway toll records. If the vehicle is returned in good condition without pending fines, 100% of your deposit is released within 24 to 48 hours.",
      "Depending on your banking provider, the credited amount will reflect in your account within 3 to 7 business days.",
    ],
  },
  {
    id: "modification-policy",
    icon: Calendar,
    title: "2. Trip Modification & Extension Policy",
    content: [
      "You can modify your pickup date, time, or vehicle selection free of charge if requested at least 12 hours prior to scheduled pickup.",
      "If you wish to extend your ongoing rental duration during the trip, requests must be submitted through the IK Travels app or website at least 4 hours before your original return time. Extensions are subject to vehicle availability and will be charged at standard daily rates.",
    ],
  },
  {
    id: "force-majeure",
    icon: AlertTriangle,
    title: "3. Special Circumstances & Technical Issues",
    content: [
      "Force Majeure Events: In case of natural disasters, severe weather alerts, government lockdowns, or flight cancellations (with proof), IK Travels provides a 100% full refund or free trip credit regardless of the cancellation timeframe.",
      "Pre-Trip Vehicle Issues: In the rare event that your reserved vehicle encounters an unexpected technical issue before pickup, we will immediately offer a free upgrade to a higher vehicle category or process a 100% instant full refund.",
    ],
  },
  {
    id: "cancellation-steps",
    icon: RotateCcw,
    title: "4. How to Request a Cancellation or Refund",
    content: [
      "Step 1: Log in to your IK Travels account and navigate to 'My Bookings'.",
      "Step 2: Select the active booking you wish to cancel and click 'Cancel Booking'.",
      "Step 3: Review the calculated refund summary on screen and confirm cancellation.",
      "Step 4: You will receive an instant SMS & Email confirmation containing your Refund Reference ID.",
      "For assistance, you can also reach our support desk at support@iktravels.com or call +91 123456789.",
    ],
  },
];

export default function CancellationAndRefundPage() {
  return (
    <SimplePageBackground>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 flex-1 flex flex-col justify-between">
        <div>
          {/* Breadcrumb Navigation */}
          <nav
            className="flex items-center flex-wrap gap-1.5 md:gap-2 text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mb-8 font-medium"
            aria-label="Breadcrumb"
          >
            <Link
              href="/"
              className="hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors"
            >
              Home
            </Link>
            <ChevronRight
              className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-600 shrink-0"
              aria-hidden
            />
            <span className="text-[#131222] dark:text-white font-semibold">
              Cancellation & Refund Policy
            </span>
          </nav>

          {/* Hero Header */}
          <div className="mb-12 text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFB51D]/10 dark:bg-[#FFB51D]/20 border border-[#FFB51D]/30 mb-4">
              <RefreshCw className="w-4 h-4 text-[#FFB51D]" aria-hidden />
              <span className="text-xs sm:text-sm font-semibold text-[#131222] dark:text-[#FFB51D]">
                Customer Protection & Terms
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#131222] dark:text-white tracking-tight mb-4">
              Cancellation & <span className="text-[#FFB51D]">Refund Policy</span>
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Transparent, fair, and hassle-free policies designed to give you complete confidence when booking self-drive cars with IK Travels.
            </p>
            <div className="h-1 w-20 bg-[#FFB51D] mx-auto rounded-full mt-5" />
          </div>

          {/* Key Highlights Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {HIGHLIGHT_CARDS.map((card, idx) => {
              const Icon = card.icon;
              return (
                <div
                  key={idx}
                  className="group rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 p-6 bg-white dark:bg-neutral-900/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-[#FFB51D]/10 dark:bg-[#FFB51D]/5 rounded-xl text-[#FFB51D] group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
                        {card.badge}
                      </span>
                    </div>
                    <h3 className="font-bold text-[#131222] dark:text-white mb-2 text-base md:text-lg">
                      {card.title}
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed">
                      {card.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cancellation Fee Matrix Table */}
          <div className="max-w-4xl mx-auto mb-14">
            <div className="rounded-2xl bg-white dark:bg-neutral-900/60 border border-neutral-200/80 dark:border-neutral-800 shadow-xl overflow-hidden">
              <div className="p-6 md:p-8 border-b border-neutral-200/80 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-800/30">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5 text-[#FFB51D]" />
                  <h2 className="text-xl md:text-2xl font-bold text-[#131222] dark:text-white">
                    Cancellation Timeline & Fee Structure
                  </h2>
                </div>
                <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                  Refund amounts are automatically calculated based on the notice time provided prior to your scheduled trip start time.
                </p>
              </div>

              <div className="divide-y divide-neutral-200/80 dark:divide-neutral-800/80">
                {CANCELLATION_TIERS.map((tier, idx) => (
                  <div
                    key={idx}
                    className="p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-neutral-50/50 dark:hover:bg-neutral-800/20 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="h-2 w-2 rounded-full bg-[#FFB51D]" />
                        <h4 className="text-base font-bold text-[#131222] dark:text-white">
                          {tier.timeframe}
                        </h4>
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 pl-4">
                        {tier.note}
                      </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 pl-4 md:pl-0">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-extrabold ${
                          tier.status === "success"
                            ? "bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                            : tier.status === "warning"
                            ? "bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                            : "bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800"
                        }`}
                      >
                        {tier.refundPercent}
                      </span>
                      <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                        ({tier.fee})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Detailed Policy Sections */}
          <div className="max-w-4xl mx-auto mb-16 space-y-6">
            {POLICY_SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.id}
                  className="group rounded-2xl bg-white dark:bg-neutral-900/30 border border-neutral-200/60 dark:border-neutral-800/60 p-6 md:p-8 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-xl bg-[#FFB51D]/10 dark:bg-[#FFB51D]/5 text-[#FFB51D] group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-[#131222] dark:text-white">
                      {section.title}
                    </h3>
                  </div>
                  <div className="space-y-3 pl-2 sm:pl-4">
                    {section.content.map((paragraph, pIdx) => (
                      <p
                        key={pIdx}
                        className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Support CTA Block */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#131222] via-neutral-900 to-[#1f1d38] p-8 md:p-10 text-white shadow-2xl border border-neutral-800">
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left max-w-xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFB51D]/20 text-[#FFB51D] text-xs font-semibold mb-3 border border-[#FFB51D]/30">
                    <HelpCircle className="w-3.5 h-3.5" /> Customer Assistance
                  </div>
                  <h3 className="text-2xl font-extrabold text-white mb-2">
                    Have questions about your refund status?
                  </h3>
                  <p className="text-neutral-300 text-sm leading-relaxed">
                    Our billing team is available to assist with refund tracking, deposit queries, or custom booking modifications.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
                  <Link
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#FFB51D] text-[#131222] font-bold text-sm hover:bg-[#FFC44D] transition-all shadow-md text-center"
                  >
                    <span>Contact Support</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    href="/faq"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/20 bg-white/10 text-white font-medium text-sm hover:bg-white/20 transition-all text-center"
                  >
                    <span>View FAQ's</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reusable Footer rendered outside container div for full width */}
      <IKFooter />
    </SimplePageBackground>
  );
}
